/**
 * Site-wide reveal on scroll.
 *
 * Every `.shopify-section` fades from 0 to 1 the first time it enters the
 * viewport. Fade only — no movement, no blur. It reveals once and never
 * replays.
 *
 * WHAT THIS FILE DOES NOT DO IS THE IMPORTANT PART. The rule that hides a
 * section lives in assets/critical.css and is gated on `html.reveal-enabled`,
 * a class added by the inline boot guard in layout/theme.liquid. THIS FILE
 * ONLY EVER REMOVES HIDING, never adds it. If it fails to load, the guard's
 * three-second deadman drops that class and the page renders normally.
 *
 * That split is deliberate and it is the whole safety story: the failure mode
 * of a reveal animation is permanently invisible content, so nothing that can
 * hide content is allowed to live in a deferred file.
 *
 * `window.themeReveal` is assigned as the FIRST statement below, before any
 * work that could throw, because the deadman checks for it by name. A slow
 * but successful load must not trip it.
 *
 * WHY THIS DOES NOT USE window.themeScroll, unlike every other component here.
 * An IntersectionObserver needs no scroll subscription — the browser does the
 * intersection work off the main thread — and the reduced-motion query is the
 * same string scroll.js uses. Consuming themeScroll would mean adopting the
 * `themescroll:ready` handshake and a load-order dependency, to gain nothing.
 * This is a deliberate divergence from the house pattern, not an oversight.
 *
 * SECTIONS THAT OPT OUT carry `reveal-skip`, set via the `class` property in
 * their {% schema %}. That is the overlay stack — cart drawer, mobile menu,
 * search panel, enquiry popup — plus the header. `opacity < 1` creates a
 * stacking context, which would trap their z-index for the length of the
 * fade. See the z-index scale in assets/critical.css.
 */
(function () {
  'use strict';

  /**
   * Claim the flag first. The boot guard in layout/theme.liquid removes
   * `reveal-enabled` after 3s if this is still undefined, so anything above
   * this line that threw would blank the page for three seconds.
   */
  var themeReveal = {};
  window.themeReveal = themeReveal;

  var ENABLED_CLASS = 'reveal-enabled';
  var SKIP_CLASS = 'reveal-skip';
  var REVEALED_ATTR = 'data-revealed';
  var SECTION_SELECTOR = '.shopify-section:not(.' + SKIP_CLASS + ')';

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var root = document.documentElement;
  var observer = null;

  /**
   * Mark a section revealed. Idempotent, and safe to call on a section that
   * was never hidden — the attribute is the only thing the CSS reads.
   */
  function reveal(section) {
    if (section.hasAttribute(REVEALED_ATTR)) return;
    section.setAttribute(REVEALED_ATTR, '');
    if (observer) observer.unobserve(section);
  }

  /**
   * Reveal everything and stop observing. Used when the visitor turns reduced
   * motion on mid-session, and as the teardown path generally.
   */
  function revealAll() {
    root.classList.remove(ENABLED_CLASS);
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    for (var i = 0; i < sections.length; i++) {
      sections[i].setAttribute(REVEALED_ATTR, '');
    }
  }

  function handleIntersections(entries) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].isIntersecting) reveal(entries[i].target);
    }
  }

  /**
   * Take charge of every section under `scope` that has not been dealt with.
   *
   * A section already touching the viewport is revealed on the NEXT FRAME
   * rather than immediately: the element has to be painted at opacity 0 once
   * for the transition to have a starting value to run from. Set the attribute
   * in the same frame and the browser coalesces both states into one style
   * recalculation, so it snaps to visible with no fade. This is what makes the
   * hero ease in on load.
   *
   * Everything below the fold goes to the observer.
   */
  function scan(scope) {
    var sections = scope.querySelectorAll(SECTION_SELECTOR);
    var pending = [];

    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      if (section.hasAttribute(REVEALED_ATTR)) continue;

      if (section.getBoundingClientRect().top < window.innerHeight) {
        pending.push(section);
      } else if (observer) {
        observer.observe(section);
      } else {
        reveal(section);
      }
    }

    if (!pending.length) return;

    requestAnimationFrame(function () {
      for (var i = 0; i < pending.length; i++) {
        reveal(pending[i]);
      }
    });
  }

  themeReveal.scan = scan;
  themeReveal.reveal = reveal;
  themeReveal.revealAll = revealAll;

  /**
   * Reduced motion: the boot guard never added the class, so nothing is
   * hidden and there is nothing to do. Marking the sections anyway keeps the
   * DOM in one consistent state whichever path ran — per the note at the top
   * of assets/scroll.js, a scroll-driven component under reduced motion should
   * render its final state, not skip the work and leave content mid-flight.
   */
  if (motionQuery.matches) {
    revealAll();
    return;
  }

  /**
   * `rootMargin` pulls the trigger line 10% up from the bottom edge, so a
   * section commits once it is meaningfully in view rather than the instant
   * its first pixel crosses. `threshold: 0` because the sections here are
   * frequently taller than the viewport, and a fractional threshold would
   * never fire for those at all.
   */
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(handleIntersections, {
      threshold: 0,
      rootMargin: '0px 0px -10% 0px',
    });
  }

  scan(document);

  /**
   * The theme editor swaps a section's markup in place. The replacement has
   * never been seen by the observer, so without this a merchant adding or
   * editing a section watches it stay blank.
   */
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section || typeof section.matches !== 'function') return;

    /*
     * The event hands us the .shopify-section wrapper itself, which its own
     * querySelectorAll cannot match — so it is dealt with directly, and scan()
     * is left to catch anything nested inside it. The editor scrolls to what it
     * just rendered, so this is always the in-view case: next frame, not this
     * one, or the fade collapses into a snap.
     */
    if (section.matches(SECTION_SELECTOR) && !section.hasAttribute(REVEALED_ATTR)) {
      requestAnimationFrame(function () {
        reveal(section);
      });
    }

    scan(section);
  });

  /**
   * Reduced motion can be switched on while the page is open. Mirrors the
   * runtime handling in assets/scroll.js, so the two never disagree about
   * which mode the site is in.
   */
  motionQuery.addEventListener('change', function (event) {
    if (event.matches) revealAll();
  });
})();
